import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import { useToastStore } from '@shared/ui/toast/useToastStore'

import { useCartInfo } from './useCartInfo'
import { useCartInfoStore } from '../model/useCartInfoStore'

import type { CartInfoVariables } from './useCartInfo'


const DEBOUNCE_CART_INFO_MS = 1000

/**
 * Centralized cartInfo fetcher with debounce.
 * Triggers a single network request on cart changes and stores the result in Zustand.
 */
export function useCartInfoManager (variables?: CartInfoVariables | null) {
  const { t } = useTranslation('errors')
  const setCartInfo = useCartInfoStore((state) => state.setCartInfo)
  const setCartInfoLoading = useCartInfoStore((state) => state.setCartInfoLoading)
  const setCartInfoError = useCartInfoStore((state) => state.setCartInfoError)
  const setRefetchCartInfo = useCartInfoStore((state) => state.setRefetchCartInfo)
  const addToast = useToastStore((state) => state.addToast)

  const inputVars = useMemo(() => variables ?? null, [ variables ])

  // Debounce variables to coalesce rapid cart updates
  const [ debouncedVars ] = useDebounceValue(inputVars, DEBOUNCE_CART_INFO_MS)

  const hasItems = !!debouncedVars?.cartInfo?.items?.length
  const hasShop = !!debouncedVars?.cartInfo?.shop_id
  const skip = !hasItems || !hasShop

  const { cartInfo, loading, error, refetch } = useCartInfo(
    debouncedVars as CartInfoVariables,
    skip,
  )

  useEffect(() => {
    setCartInfoLoading(!skip && loading)
  }, [ loading, setCartInfoLoading, skip ])

  useEffect(() => {
    setCartInfo(skip ? null : cartInfo ?? null)
  }, [ cartInfo, setCartInfo, skip ])

  useEffect(() => {
    setCartInfoError(skip ? null : error ?? null)
    if (!skip && error) {
      addToast({
        message: t('cart_info_error'),
        variant: 'error',
      })
    }
  }, [ error, setCartInfoError, addToast, skip, t ])

  useEffect(() => {
    setRefetchCartInfo(skip ? null : refetch ?? null)
    return () => setRefetchCartInfo(null)
  }, [ refetch, setRefetchCartInfo, skip ])
}
